<template>
  <slice
    class="slice--purchase"
    :class="{ 'has-modal': modal }"
    @in="$emit('in')"
    @out="$emit('out')"
  >
    <div
      class="d-md-flex justify-center align-start inner p-relative"
      data-v-sticky-container
    >
      <div
        class="flex-grow-1 p-relative"
        :class="{ 'only-on-image-container': onlyOneImage }"
      >
        <transition
          v-if="selectedColorHasASlider"
          name="gallery-switch"
          mode="in-out"
          @beforeEnter="sliderSwitchBeforeEnter"
          @enter="sliderSwitchEnter"
          @leave="sliderSwitchLeave"
        >
          <gallery
            :key="selectedColor._key"
            ref="gallery"
            :slider="selectedColor.slider"
            data-cy-gallery-image
          />
        </transition>
        <div v-else-if="onlyOneImage" class="only-one-image">
          <lazy-image
            v-if="productDocument.type === TYPES.GIFTCARD"
            :image="firstImage.highlight"
          />
          <lazy-image
            v-else
            :image="firstImage.vertical ? firstImage.vertical : firstImage"
            :widths="[640, 960, 1280]"
            preload
            force-show
          />
        </div>
        <div v-else class="skeleton" style="min-height: 100vh"></div>
      </div>

      <div v-sticky="sticky" class="slice--purchase__purchase">
        <purchase-gift-card
          v-if="productDocument.type === TYPES.GIFTCARD"
          ref="purchase"
          :product-document="productDocument"
          :shopify-product="shopifyProduct"
          data-v-sticky-inner
        />

        <purchase-suit
          v-else-if="productDocument.is_suit"
          ref="purchase"
          :product-document="productDocument"
          :shopify-product="shopifyProduct"
          :with-testimonials="withTestimonials"
          :product-variant-updated="productVariantUpdated"
          data-v-sticky-inner
          @update-color="setColor"
          @update-variant="setSuitVariant"
          @show-modal="showModal"
        />

        <purchase
          v-else
          ref="purchase"
          :product-document="productDocument"
          :shopify-product="shopifyProduct"
          :with-testimonials="withTestimonials"
          :product-variant-updated="productVariantUpdated"
          :additionnal-inventory="additionnalInventory"
          :slider-abtest-color="sliderAbtestColor"
          :product-ab-test="productAbTest"
          data-v-sticky-inner
          @update-color="setColor"
          @update-variant="setVariantUrlParam"
          @update-dimension="setDimensionInUrl"
          @show-modal="showModal"
        />
      </div>
    </div>

    <modal
      v-if="modal"
      :data="modalData"
      :active-size="modalActiveSize"
      @close="modal = false"
    />
  </slice>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import slugify from 'slugify'

import Slice from '@/components/Slices/Slice'
import LazyImage from '@/components/Image/LazyImage'
import Purchase from '@/components/Purchase/Purchase'
import PurchaseSuit from '@/components/Purchase/PurchaseSuit'
import PurchaseGiftCard from '@/components/Purchase/PurchaseGiftCard'
import Gallery from '@/components/gallery/Gallery'

import Product from '@/entities/shopify/Product'

import { TYPES, STATUS } from '@/const'
import { EVENTS } from '@/const/events'

import services from '@/services'

const Modal = () => ({
  component: import('@/components/modal/Modal' /* webpackChunkName: 'modal' */),
  timeout: 3000,
})

export default {
  name: 'PurchaseModuleSlice',
  components: {
    Slice,
    LazyImage,
    Modal,
    Purchase,
    PurchaseSuit,
    PurchaseGiftCard,
    Gallery,
  },
  props: {
    productDocument: { type: Object, required: true },
    shopifyProduct: { type: Product, required: true },
    additionnalInventory: { type: Number, default: 0 },
    withTestimonials: {
      type: Boolean,
      default: false,
    },
    productUpdated: {
      type: Boolean,
      required: true,
    },
    alternativeSliders: {
      type: Array,
      default: () => [],
    },
    productAbTest: { type: Boolean, default: null },
  },
  data() {
    return {
      selectedColor: null,
      sticky: {
        containerSelector: '[data-v-sticky-container]',
        innerWrapperSelector: '[data-v-sticky-inner]',
        resizeSensor: true,
        minWidth: 769,
      },
      loadingModal: false,
      modal: false,
      modalData: null,
      modalActiveSize: '',
      productVariantUpdated: false,
      sliderAbtestColor: null,
      TYPES,
      STATUS,
    }
  },
  computed: {
    ...mapGetters({
      campaigns: 'abtest/campaigns',
      isCampaignsLoading: 'abtest/isLoading',
    }),
    hasSelectedColor() {
      return (
        this.selectedColor &&
        this.selectedColor.constructor === Object &&
        Object.keys(this.selectedColor).length > 0
      )
    },
    selectedColorHasASlider() {
      return this.hasSelectedColor && this.selectedColor.slider
    },
    onlyOneImage() {
      if (this.productDocument.colors.length === 0) {
        return true
      }

      if (this.selectedColor) {
        if (!this.selectedColorHasASlider) {
          return true
        }

        if (this.selectedColor.slider.elements.length === 1) {
          return true
        }
      }

      return false
    },
    firstImage() {
      if (!this.onlyOneImage) {
        return undefined
      }
      if (
        this.productDocument.type === TYPES.GIFTCARD ||
        this.productDocument.colors.length === 0
      ) {
        return this.productDocument.image
      }
      if (this.productDocument.colors.length === 1) {
        return this.productDocument.colors[0].image_principale
      }

      return this.selectedColor.image_principale
    },
    hasThirdDimension() {
      return (
        this.productDocument.shopify_product.options.length > 2 &&
        this.productDocument.shopify_product.options.some(
          (o) => o.position === 3
        )
      )
    },
    thirdDimensionName() {
      if (this.hasThirdDimension) {
        return slugify(
          this.productDocument.shopify_product.options.find(
            (o) => o.position === 3
          ).name,
          { lower: true, strict: true }
        )
      }

      return ''
    },
  },
  created() {
    if (process.server) {
      if (this.productDocument.colors.length > 0) {
        this.selectedColor = this.productDocument.colors[0]
      }
    }
  },
  mounted() {
    if (this.productUpdated) {
      this.onProductUpdated()
    } else {
      this.$nuxt.$on(EVENTS.PRODUCT_UPDATED, this.onProductUpdated)
    }

    if (
      this.productDocument.type === TYPES.GIFTCARD &&
      this.productDocument.colors.length > 0
    ) {
      this.selectedColor = this.productDocument.colors[0]
    }
  },
  beforeDestroy() {
    this.$nuxt.$off(EVENTS.PRODUCT_UPDATED, this.onProductUpdated)
  },
  methods: {
    ...mapActions({
      activateTest: 'abtest/activate',
    }),
    ...mapMutations({
      setScroll: 'behavior/SET_SCROLL',
    }),
    onProductUpdated() {
      this.$nuxt.$emit(EVENTS.PRODUCT_VARIANT_UPDATED)
      this.productVariantUpdated = true
    },
    setColor(color, fromUserAction, third = false) {
      if (third) {
        if (
          this.selectedColor &&
          this.selectedColor._key === `${third}__${color}`
        ) {
          return
        }
      } else if (this.selectedColor && this.selectedColor._key === color) {
        return
      }

      if (this.productDocument.colors.length > 0) {
        if (
          !this.selectedColor ||
          this.selectedColor._key !== color ||
          (third && this.selectedColor._key !== `${third}__${color}`)
        ) {
          if (third) {
            this.selectedColor = this.productDocument.colors.find(
              (c) => c._key === `${third}__${color}`
            )
          } else {
            this.selectedColor = this.productDocument.colors.find(
              (c) => c._key === color
            )
          }

          if (fromUserAction) {
            this.$track.colorChange(color, this.productDocument.uid)
            this.setDimensionInUrl('color', color)
            if (third && this.thirdDimensionName) {
              this.setDimensionInUrl(this.thirdDimensionName, third)
            }
          }
        }
      }

      if (fromUserAction) {
        this.scrollToTop()

        if (this.$refs.gallery) {
          this.$refs.gallery.onColorChange()
        }
      }

      if (
        process.client &&
        this.productDocument.type !== TYPES.GIFTCARD &&
        this.hasSelectedColor
      ) {
        this.handleActivateABTest(this.selectedColor.name)
      }
    },
    handleActivateABTest(color) {
      const uid = this.productDocument.uid

      let sliderColor = null
      if (
        !this.isCampaignsLoading &&
        this.campaigns.hasProductSliderTest(uid)
      ) {
        const productCampaigns = this.campaigns.getProductSliderTest(uid)

        for (const campaign of productCampaigns) {
          if (campaign.color === color) {
            this.activateTest({
              campaignName: campaign.campaignName,
              variationId: campaign.variation.id,
              variationName: campaign.variation.name,
              variationGroupId: campaign.variationGroupId,
            })

            sliderColor = color

            break
          }
        }
      }

      this.sliderAbtestColor = sliderColor
    },
    setVariantUrlParam(variant) {
      if (process.client) {
        const url = new URL(window.location.href)

        if (variant) {
          url.searchParams.delete('color')
          if (this.thirdDimensionName) {
            url.searchParams.delete(this.thirdDimensionName)
          }
          url.searchParams.set('variant', variant.id)
        } else {
          url.searchParams.delete('variant')
        }

        window.history.replaceState(null, null, url)
      }
    },
    setDimensionInUrl(dimension, value) {
      if (process.client) {
        const url = new URL(window.location.href)

        if (value && !url.searchParams.has('variant')) {
          url.searchParams.set(dimension, value)
        } else {
          url.searchParams.delete(dimension)
        }

        window.history.replaceState(null, null, url)
      }
    },
    setSuitVariant(jacket, pant) {
      if (process.client) {
        const url = new URL(window.location.href)
        if (jacket) {
          url.searchParams.set('jacket', jacket.id)
          window.history.replaceState(null, null, url)
        } else {
          url.searchParams.delete('jacket')
          window.history.replaceState(null, null, url)
        }

        if (pant) {
          url.searchParams.set('pant', pant.id)
          window.history.replaceState(null, null, url)
        } else {
          url.searchParams.delete('pant')
          window.history.replaceState(null, null, url)
        }
      }
    },
    setGiftCardVariant(variant) {
      if (variant) {
        this.setColor(`${variant.price}€`)
        this.setVariantUrlParam(variant)
      }
    },
    async scrollToTop() {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)
      const { ScrollToPlugin } = await import(
        'gsap/ScrollToPlugin' /* webpackChunkName: 'gsap_scroll_to_plugin' */
      )
      gsap.registerPlugin(ScrollToPlugin)

      this.setScroll(true)
      gsap.to(window, {
        duration: 0,
        scrollTo: { y: this.$el, offsetY: -1 },
        onComplete: () => {
          this.setScroll(false)
        },
      })
    },
    showModal(type, activeSize) {
      if (!this.loadingModal) {
        let id = null

        if (type === 'exchange' && this.productDocument.delivery_and_returns) {
          id = this.productDocument.delivery_and_returns._ref
        } else if (type === 'size-guide' && this.productDocument.size_guide) {
          id = this.productDocument.size_guide._ref
        }

        if (id) {
          this.loadingModal = true
          services.sanityService
            .getById({
              id,
              lang: this.$i18n.locale.slice(0, 2),
            })

            .then((document) => {
              this.modalData = document
              this.modalActiveSize = activeSize
              this.modal = true
            })
            .catch((err) => {
              this.$logError.captureException(err)
            })
            .finally(() => {
              this.loadingModal = false
            })
        }
      }
    },
    async sliderSwitchBeforeEnter(el) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      if (this.$el.querySelectorAll('.gallery').length > 1) {
        gsap.set(el, {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
        })
      }
    },
    async sliderSwitchEnter(el, done) {
      if (this.$el.querySelectorAll('.gallery').length === 1) {
        done()
        return
      }
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.from(el, {
        duration: 0.5,
        opacity: 0,
        force3D: false,
        onComplete: () => {
          gsap.set(el, {
            position: '',
            top: '',
            left: '',
            right: '',
          })
          done()
        },
      })
    },
    async sliderSwitchLeave(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.set(el, {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: -1,
      })

      gsap.to(el, {
        duration: 0,
        delay: 0.1,
        opacity: 0,
        force3D: false,
        onComplete: done,
      })
    },
  },
}
</script>

<style lang="scss">
.slice--purchase {
  --purchase-width: #{rem(450px)};

  position: relative;
  overflow: hidden;
  z-index: 10;

  @include mq($until: tablet) {
    padding: 0;
  }

  .gallery {
    will-change: opacity;
    background: var(--tertiary-color);
  }

  &.has-modal {
    z-index: unset;
  }

  &__purchase {
    .purchase {
      padding: var(--spacing);
    }

    @include mq($from: desktop) {
      width: var(--purchase-width);
      flex-shrink: 0;

      .purchase {
        padding: calc(var(--spacing) * 3) var(--spacing);
      }
    }

    @include mq($from: tablet) {
      &.is-affixed {
        .purchase {
          padding-bottom: calc(var(--spacing) * 5);
        }
      }
    }
  }
}

.only-one-image {
  width: 100%;
  height: 100%;
  max-width: rem(640px);
  margin: auto;

  @include mq($from: tablet) {
    min-height: 80vh;
  }
}

.only-on-image-container {
  @include mq($from: tablet) {
    align-self: stretch;

    .gallery,
    .gallery__inner,
    .lazy-image-container {
      height: 100%;
    }

    .gallery__inner {
      grid-template-rows: minmax(100%, var(--lines-desktop));
    }

    .lazy-image-container,
    .lazy-image {
      background: transparent;
    }

    .lazy-image {
      padding-top: 0 !important;
      height: 100%;

      img {
        width: auto;
        min-height: 100%;
        max-width: none;
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }
}

.purchase {
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  transition: margin 0.3s, padding 0.3s, min-height 0.3s;

  @include mq($from: 1381px) {
    margin: 0 auto;
  }

  h1 {
    font-size: var(--h2-font-size);
    margin-bottom: rem($spacing);

    @include mq($from: tablet, $until: large) {
      font-size: var(--h3-font-size);
    }
  }

  .link-cta {
    justify-content: center;
  }

  &__top {
    @include mq($from: tablet) {
      width: 100%;
    }
  }

  &__form {
    width: 100%;
    position: relative;
    margin: 0 auto;
    box-sizing: border-box;
  }

  .create-alert {
    background: var(--secondary-color);
    color: var(--tertiary-color);
    padding: rem($spacing * 2);

    .input__line {
      background: $yellow;
      height: rem(3px);
      max-width: calc(100% - #{rem(1px)});
    }

    .action {
      width: 100%;
      margin-top: rem($spacing);

      @include mq($from: tablet) {
        height: rem(45px);
        padding: 0;
      }
    }
  }

  &__informations {
    .child {
      max-width: rem(120px);

      &:first-child {
        padding-right: rem($spacing);
      }

      &:last-child {
        padding-left: rem($spacing);
      }
    }

    .child--with-date {
      max-width: rem(152px);

      &:first-child {
        margin-right: rem(11px);
      }
    }

    .child--with-batch {
      max-width: rem(230px);

      &:first-child {
        margin-right: rem(11px);
      }
    }

    .countdown--soon {
      .countdown__before {
        font-size: var(--small-font-size);
        line-height: 1;

        @include mq($from: tablet) {
          font-size: var(--base-font-size);
        }
      }
    }
  }

  @include mq($from: tablet) {
    .product-price {
      flex-grow: 1;
    }
  }
}
</style>
